import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Volume2, VolumeX, Music, Loader } from 'lucide-react';

export const AmbientAudio = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.4);
  const [showControls, setShowControls] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioContextRef = useRef(null);
  const gainNodeRef = useRef(null);
  const isPlayingRef = useRef(false);
  const schedulerRef = useRef(null);

  // Musical notes frequencies (pentatonic scale for peaceful sound)
  const notes = {
    C3: 130.81, D3: 146.83, E3: 164.81, G3: 196.00, A3: 220.00,
    C4: 261.63, D4: 293.66, E4: 329.63, G4: 392.00, A4: 440.00,
    C5: 523.25, D5: 587.33, E5: 659.25, G5: 783.99, A5: 880.00,
  };

  const pentatonic = ['C', 'D', 'E', 'G', 'A'];
  
  const getRandomNote = (octave) => {
    const note = pentatonic[Math.floor(Math.random() * pentatonic.length)];
    return notes[`${note}${octave}`];
  };

  const createReverb = useCallback(async (ctx) => {
    const convolver = ctx.createConvolver();
    const length = ctx.sampleRate * 3;
    const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 2);
      }
    }
    
    convolver.buffer = impulse;
    return convolver;
  }, []);

  const playNote = useCallback((ctx, freq, startTime, duration, type = 'sine') => {
    const osc = ctx.createOscillator();
    const envelope = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    osc.type = type;
    osc.frequency.setValueAtTime(freq, startTime);
    
    // Soft filter for warmth
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(2000, startTime);
    filter.Q.setValueAtTime(1, startTime);
    
    // Gentle envelope
    envelope.gain.setValueAtTime(0, startTime);
    envelope.gain.linearRampToValueAtTime(0.15, startTime + 0.3);
    envelope.gain.setValueAtTime(0.15, startTime + duration - 0.5);
    envelope.gain.linearRampToValueAtTime(0, startTime + duration);
    
    osc.connect(filter);
    filter.connect(envelope);
    envelope.connect(gainNodeRef.current);
    
    osc.start(startTime);
    osc.stop(startTime + duration);
  }, []);

  const playChord = useCallback((ctx, baseFreq, startTime, duration) => {
    // Root note
    playNote(ctx, baseFreq, startTime, duration, 'sine');
    // Fifth (perfect fifth ratio)
    playNote(ctx, baseFreq * 1.5, startTime + 0.1, duration - 0.1, 'sine');
    // Octave
    playNote(ctx, baseFreq * 2, startTime + 0.2, duration - 0.2, 'triangle');
  }, [playNote]);

  const playMelodyNote = useCallback((ctx, startTime) => {
    const freq = getRandomNote(Math.random() > 0.3 ? 5 : 4);
    const osc = ctx.createOscillator();
    const envelope = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startTime);
    
    // Add slight vibrato
    const vibrato = ctx.createOscillator();
    const vibratoGain = ctx.createGain();
    vibrato.frequency.setValueAtTime(5, startTime);
    vibratoGain.gain.setValueAtTime(3, startTime);
    vibrato.connect(vibratoGain);
    vibratoGain.connect(osc.frequency);
    vibrato.start(startTime);
    vibrato.stop(startTime + 4);
    
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1500, startTime);
    
    const duration = 2 + Math.random() * 2;
    envelope.gain.setValueAtTime(0, startTime);
    envelope.gain.linearRampToValueAtTime(0.08, startTime + 0.5);
    envelope.gain.setValueAtTime(0.08, startTime + duration - 1);
    envelope.gain.linearRampToValueAtTime(0, startTime + duration);
    
    osc.connect(filter);
    filter.connect(envelope);
    envelope.connect(gainNodeRef.current);
    
    osc.start(startTime);
    osc.stop(startTime + duration);
  }, []);

  const playPad = useCallback((ctx, startTime) => {
    const baseNotes = [notes.C3, notes.E3, notes.G3, notes.C4];
    const selectedBase = baseNotes[Math.floor(Math.random() * baseNotes.length)];
    
    playChord(ctx, selectedBase, startTime, 8);
  }, [playChord, notes]);

  const startAmbientMusic = useCallback(async () => {
    if (audioContextRef.current) return;
    
    setIsLoading(true);
    
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
      
      // Master gain
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(volume, ctx.currentTime);
      gainNodeRef.current = masterGain;
      
      // Reverb
      const reverb = await createReverb(ctx);
      const reverbGain = ctx.createGain();
      reverbGain.gain.setValueAtTime(0.3, ctx.currentTime);
      
      // Dry/wet mix
      const dryGain = ctx.createGain();
      dryGain.gain.setValueAtTime(0.7, ctx.currentTime);
      
      masterGain.connect(dryGain);
      masterGain.connect(reverb);
      reverb.connect(reverbGain);
      dryGain.connect(ctx.destination);
      reverbGain.connect(ctx.destination);
      
      isPlayingRef.current = true;
      
      // Schedule music
      let nextPadTime = ctx.currentTime;
      let nextMelodyTime = ctx.currentTime + 1;
      
      const scheduler = () => {
        if (!isPlayingRef.current) return;
        
        const currentTime = ctx.currentTime;
        const lookAhead = 2;
        
        // Schedule pads (every 6-10 seconds)
        while (nextPadTime < currentTime + lookAhead) {
          playPad(ctx, nextPadTime);
          nextPadTime += 6 + Math.random() * 4;
        }
        
        // Schedule melody notes (every 2-4 seconds)
        while (nextMelodyTime < currentTime + lookAhead) {
          if (Math.random() > 0.3) {
            playMelodyNote(ctx, nextMelodyTime);
          }
          nextMelodyTime += 2 + Math.random() * 2;
        }
        
        schedulerRef.current = setTimeout(scheduler, 500);
      };
      
      scheduler();
      setIsLoading(false);
      setIsPlaying(true);
      
    } catch (error) {
      console.error('Failed to start audio:', error);
      setIsLoading(false);
    }
  }, [volume, createReverb, playPad, playMelodyNote]);

  const stopAmbientMusic = useCallback(() => {
    isPlayingRef.current = false;
    
    if (schedulerRef.current) {
      clearTimeout(schedulerRef.current);
      schedulerRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
      gainNodeRef.current = null;
    }
    
    setIsPlaying(false);
  }, []);

  const togglePlay = async () => {
    if (isPlaying) {
      stopAmbientMusic();
    } else {
      await startAmbientMusic();
    }
  };

  useEffect(() => {
    if (gainNodeRef.current && audioContextRef.current) {
      gainNodeRef.current.gain.setValueAtTime(volume, audioContextRef.current.currentTime);
    }
  }, [volume]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAmbientMusic();
    };
  }, [stopAmbientMusic]);

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  return (
    <>
      {/* Floating Audio Control Button */}
      <div 
        className="fixed bottom-6 left-6 z-50"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
      >
        {/* Expanded Controls */}
        <div 
          className={`absolute bottom-16 left-0 mb-2 transition-all duration-300 ${
            showControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
          }`}
        >
          <div className="bg-[#0f1a2a]/95 backdrop-blur-xl border border-[#2a3a4d] rounded-lg p-4 shadow-xl min-w-[200px]">
            {/* Title */}
            <div className="flex items-center gap-2 mb-3">
              <Music size={14} className="text-[#c9a87c]" />
              <p className="text-xs text-[#c9a87c] uppercase tracking-wider">Ambient Music</p>
            </div>
            
            {/* Description */}
            <p className="text-xs text-[#6b7c93] mb-3">
              Calm piano melodies for focus
            </p>
            
            {/* Volume Slider */}
            <div className="flex items-center gap-3">
              <VolumeX size={14} className="text-[#5a7fa8]" />
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={volume}
                onChange={handleVolumeChange}
                className="flex-1 h-1 bg-[#2a3a4d] rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:w-3
                  [&::-webkit-slider-thumb]:h-3
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:bg-[#c9a87c]
                  [&::-webkit-slider-thumb]:cursor-pointer
                  [&::-webkit-slider-thumb]:transition-transform
                  [&::-webkit-slider-thumb]:hover:scale-125"
              />
              <Volume2 size={14} className="text-[#5a7fa8]" />
            </div>

            {/* Status */}
            <p className="text-xs text-[#5a7fa8] mt-3 text-center">
              {isLoading ? 'Starting...' : isPlaying ? '♪ Playing' : 'Click to Play'}
            </p>
          </div>
        </div>

        {/* Main Button */}
        <button
          onClick={togglePlay}
          disabled={isLoading}
          className={`group relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-500 cursor-pointer ${
            isPlaying 
              ? 'bg-[#0f1a2a] border-2 border-[#c9a87c] shadow-[0_0_25px_rgba(201,168,124,0.4)]' 
              : 'bg-[#0f1a2a]/90 border border-[#2a3a4d] hover:border-[#5a7fa8] hover:shadow-[0_0_15px_rgba(90,127,168,0.3)]'
          } ${isLoading ? 'cursor-wait' : ''}`}
          aria-label={isPlaying ? 'Pause ambient music' : 'Play ambient music'}
        >
          {/* Pulsing rings when playing */}
          {isPlaying && (
            <>
              <span className="absolute inset-0 rounded-full border border-[#c9a87c] animate-ping opacity-20" />
              <span className="absolute inset-[-6px] rounded-full border border-[#c9a87c]/20 animate-pulse" />
            </>
          )}
          
          {/* Icon */}
          {isLoading ? (
            <Loader size={20} className="text-[#c9a87c] animate-spin" />
          ) : (
            <Music 
              size={20} 
              className={`transition-all duration-300 ${
                isPlaying ? 'text-[#c9a87c] scale-110' : 'text-[#5a7fa8] group-hover:text-[#c9a87c]'
              }`} 
            />
          )}

          {/* Musical notes animation when playing */}
          {isPlaying && !isLoading && (
            <div className="absolute -right-1 -top-1 flex gap-0.5">
              <span className="w-0.5 bg-[#c9a87c] rounded-full animate-bounce" style={{ height: '8px', animationDelay: '0ms', animationDuration: '1s' }} />
              <span className="w-0.5 bg-[#c9a87c] rounded-full animate-bounce" style={{ height: '12px', animationDelay: '200ms', animationDuration: '1s' }} />
              <span className="w-0.5 bg-[#c9a87c] rounded-full animate-bounce" style={{ height: '6px', animationDelay: '400ms', animationDuration: '1s' }} />
            </div>
          )}
        </button>
      </div>
    </>
  );
};

export default AmbientAudio;
